// @flow
import React, { useRef, useEffect, useState, useCallback, useMemo, useReducer } from "react";
import Toast from "cogo-toast";
import memoizeOne from "memoize-one";
import Tooltip from "react-simple-tooltip";
import { GlobalHotKeys } from "react-hotkeys";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

const keyMap = { jumpToPreviousSynapse: "left", jumpToNextSynapse: "right" };

const X_AXIS_HEIGHT = 24;
const Y_AXIS_WIDTH = 24;
const AXIS_DESCRIPTION_HEIGHT = 24;
const WHOLE_HORIZONTAL_MARGIN_OF_CONTENT = 72;
const getEffectColor = alpha => `rgba(20, 200, 20, ${alpha})`;
const INVALID_ENTRY = -1;

// Fix JS modulo bug
// http://javascript.about.com/od/problemsolving/a/modulobug.htm
function mod(x, n) {
  return ((x % n) + n) % n;
}

function getMatrixWidth() {
  return Math.floor((window.innerWidth - WHOLE_HORIZONTAL_MARGIN_OF_CONTENT) / 2.4);
}
const getMatrixHeight = getMatrixWidth;
const getIFrameWidth = () => getMatrixWidth() + Y_AXIS_WIDTH + AXIS_DESCRIPTION_HEIGHT;
function getMinimapWidth() {
  return Math.floor((window.innerWidth - WHOLE_HORIZONTAL_MARGIN_OF_CONTENT) / 9.5);
}

function getUsageInstructionFontSize() {
  return Math.min(Math.floor(window.innerWidth / 130), 24);
}

type Synapse = {
  position: [number, number, number],
  type: number,
};

type Connection = {
  axon: number,
  dend: number,
  synapses: Array<Synapse>,
};

type ConnectomeData = {
  axonClasses: Array<string>,
  dendClasses: Array<string>,
  axons: Array<[number, number]>,
  dends: Array<[number, number]>,
  connections: Array<Connection>,
  synapseTypes: Array<string>,
};

type RectangleSize = {
  x: number,
  y: number,
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getColorValueForSynapseCount(synapseCount) {
  if (synapseCount === 1) {
    return [69, 58, 150];
  }
  if (synapseCount === 2) {
    return [76, 115, 183];
  }
  if (synapseCount === 3) {
    return [32, 187, 183];
  }
  if (synapseCount === 4) {
    return [199, 193, 46];
  }
  if (synapseCount > 4) {
    return [242, 233, 38];
  }
  return [0, 0, 0];
}

function calculateRectangleSizes(width: number, height: number, size: number): RectangleSize {
  // We make sure that the rectangle is shorter in the direction
  // of the shortest dimension as this dimension will be scaled up.
  let sizeX = size;
  let sizeY = size;
  if (width > height) {
    sizeY = (sizeY * height) / width;
  } else {
    sizeX = (sizeX * width) / height;
  }
  return { x: Math.max(1, sizeX), y: Math.max(1, sizeY) };
}

function splitRectangleSizeIntoIntegerHalves(
  rectangleSize: RectangleSize,
): {
  halfWidthLeft: number,
  halfHeightLeft: number,
  halfWidthRight: number,
  halfHeightRight: number,
} {
  const { x: width, y: height } = rectangleSize;
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const halfWidthLeft = Math.floor(halfWidth);
  const halfHeightLeft = Math.floor(halfHeight);
  const halfWidthRight = Math.ceil(halfWidth);
  const halfHeightRight = Math.ceil(halfHeight);

  return { halfWidthLeft, halfHeightLeft, halfWidthRight, halfHeightRight };
}

const getConnectomeMatrixImageWithMap = memoizeOne(
  (connections, width, height, connectionRectangleSize) => {
    const imageData = new Uint8Array(width * height * 4).fill(0);

    // Saves for each pixel / position in the image the matching connection index.
    const positionToConnectionMap = new Int32Array(width * height).fill(INVALID_ENTRY);
    for (let i = 0; i < width * height; i++) {
      // Set alpha value everywhere to max.
      imageData[4 * i + 3] = 255;
    }

    const {
      halfWidthLeft,
      halfHeightLeft,
      halfWidthRight,
      halfHeightRight,
    } = splitRectangleSizeIntoIntegerHalves(connectionRectangleSize);

    const setColorAtPosition = (axonIndex, dendIndex, color, index) => {
      const axonStart = Math.max(0, axonIndex - halfHeightLeft);
      const axonEnd = Math.min(height, axonIndex + halfHeightRight);
      const dendStart = Math.max(0, dendIndex - halfWidthLeft);
      const dendEnd = Math.min(width, dendIndex + halfWidthRight);
      for (let i = axonStart; i < axonEnd; ++i) {
        for (let j = dendStart; j < dendEnd; ++j) {
          const currentPosition = width * i + j;
          imageData[currentPosition * 4] = color[0];
          imageData[currentPosition * 4 + 1] = color[1];
          imageData[currentPosition * 4 + 2] = color[2];
          positionToConnectionMap[currentPosition] = index;
        }
      }
    };
    connections.forEach((connection, index) => {
      const axonIndex = connection.axon;
      const dendIndex = connection.dend;
      const color = getColorValueForSynapseCount(connection.synapses.length);
      setColorAtPosition(axonIndex, dendIndex, color, index);
    });

    return [imageData, positionToConnectionMap];
  },
);

const evtPositionToCanvasPosition = (evtPosition, canvas, scale) => {
  const rect = canvas.getBoundingClientRect();
  const x = evtPosition.x - rect.left;
  const y = evtPosition.y - rect.top;
  const [scaledX, scaledY] = [Math.floor(x / scale.x), Math.floor(y / scale.y)];
  return { x: scaledX, y: scaledY };
};

function useFetch<T>(fetchFn: () => Promise<T>, defaultValue: T, dependencies: Array<any>): T {
  const [value, setValue] = useState(defaultValue);

  const fetchValue = async () => {
    const fetchedValue = await fetchFn();
    setValue(fetchedValue);
  };

  useEffect(() => {
    fetchValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
  return value;
}

const fetchData = async (): Promise<ConnectomeData> => (await fetch("data.json")).json();

const fetchAxonNml = id => fetch(`axon_skeletons/axon_${id}.nml`);

// This function is used to distinguish xml/nml files from html files.
const checkForNml = text => text.startsWith(`<?xml version="1.0" encoding="utf-8"?>`);

const fetchDendNml = id => fetch(`dend_skeletons/dend_${id}.nml`);

const fetchSynapsesNml = (axonId, dendId) => fetch(`synapses/synapses_of_${axonId}_${dendId}.nml`);

const drawConnectome = (
  connectomeCanvas,
  data,
  setPositionToConnectionMap,
  connectionRectangleSize,
) => {
  if (connectomeCanvas.current == null) return;
  const matrixWidth = data.dends.length;
  const matrixHeight = data.axons.length;
  const ctx = connectomeCanvas.current.getContext("2d");
  const imageData = ctx.createImageData(matrixWidth, matrixHeight);
  const [contactData, positionToConnectionMap] = getConnectomeMatrixImageWithMap(
    data.connections,
    matrixWidth,
    matrixHeight,
    connectionRectangleSize,
  );
  setPositionToConnectionMap(positionToConnectionMap);
  imageData.data.set(contactData);
  ctx.putImageData(imageData, 0, 0);
};

const drawHighlightedConnection = (
  connectomeCanvas,
  connectionRectangleSize,
  lastClickPos,
  lastHighlight,
  setLastHighlight,
) => {
  // This method highlights the currently clicked connection by saving
  // the imageData at the click position and then painting it red.
  // If the user clicks somewhere else, it restores the last highlighted
  // position to its original state, before saving and painting the new one.
  const samePosition =
    lastClickPos.x === lastHighlight.position.x && lastClickPos.y === lastHighlight.position.y;
  if (connectomeCanvas.current == null || samePosition) return;
  const ctx = connectomeCanvas.current.getContext("2d");

  const {
    halfWidthLeft,
    halfHeightLeft,
    halfWidthRight,
    halfHeightRight,
  } = splitRectangleSizeIntoIntegerHalves(connectionRectangleSize);

  const { position, imageData } = lastHighlight;
  if (imageData != null) {
    // Restore the last highlight to its original state
    ctx.putImageData(imageData, position.x - halfWidthLeft, position.y - halfHeightLeft);
  }

  const { x, y } = lastClickPos;
  // Save the original state before painting the highlight red
  const imageDataAtClickPos = ctx.getImageData(
    x - halfWidthLeft,
    y - halfHeightLeft,
    halfWidthLeft + halfWidthRight,
    halfHeightLeft + halfHeightRight,
  );

  const highlightImageData = ctx.createImageData(
    halfWidthLeft + halfWidthRight,
    halfHeightLeft + halfHeightRight,
  );
  for (let ind = 0; ind < highlightImageData.data.length; ind += 4) {
    // Set to red and full alpha
    highlightImageData.data[ind] = 255;
    highlightImageData.data[ind + 3] = 255;
  }
  ctx.putImageData(highlightImageData, x - halfWidthLeft, y - halfHeightLeft);

  setLastHighlight({ position: { ...lastClickPos }, imageData: imageDataAtClickPos });
};

const drawMinimap = (connectomeCanvas, minimapCanvas, minimapWidth) => {
  const currentConnectomeCanvas = connectomeCanvas.current;
  if (currentConnectomeCanvas == null || minimapCanvas.current == null) {
    return;
  }

  const ctx = minimapCanvas.current.getContext("2d");
  // We need a delay here as the connectome canvas might not finished rendering. Thus leading to an empty minimap.
  setTimeout(() => ctx.drawImage(currentConnectomeCanvas, 0, 0, minimapWidth, minimapWidth), 500);
};

function getClassIntervalsOf(dataArray, classLabels) {
  const classIntervals = [];
  let intervalStart = 0;
  for (let intervalEnd = intervalStart; intervalEnd < dataArray.length; intervalEnd++) {
    // If we are at the end of the array or the class of the next axon / dendrite is different, the interval ends here.
    if (
      intervalEnd === dataArray.length - 1 ||
      dataArray[intervalStart][0] !== dataArray[intervalEnd + 1][0]
    ) {
      classIntervals.push({
        from: intervalStart,
        to: intervalEnd,
        classLabel: classLabels[dataArray[intervalStart][0]],
      });
      intervalStart = intervalEnd + 1;
    }
  }
  return classIntervals;
}

const renderCaptionOfAxis = (intervals, scale, highlightedPosition, mirrorLetters) => {
  return intervals.map((interval, index) => {
    const { from, to, classLabel } = interval;
    // The to interval end is inclusive
    const width = to - from + 1;
    // We need to subtract the width of border from the width. The first and last element have a wider border.
    const totalBorderWidth = index === 0 || index === intervals.length - 1 ? 3 : 2;
    let color = "black";
    if (from <= highlightedPosition && highlightedPosition <= to) {
      color = getEffectColor(1);
    }
    return (
      <div
        key={classLabel}
        style={{
          width: width * scale - totalBorderWidth,
          borderColor: color,
        }}
      >
        <div
          style={{
            left: 0,
            right: 0,
            position: "sticky",
            display: "inline-block",
            transform: mirrorLetters ? "scale(-1, 1)" : "",
            color,
            fontSize: 12,
          }}
          title={classLabel}
        >
          {classLabel.substring(0, 2)}
        </div>
      </div>
    );
  });
};

const drawEffects = (effectCanvas, hoverPos, effectScale) => {
  if (effectCanvas.current == null) {
    return;
  }
  const hoverPosInEffectCanvas = evtPositionToCanvasPosition(
    hoverPos,
    effectCanvas.current,
    effectScale,
  );
  const crosshairGap = 6;
  const ctx = effectCanvas.current.getContext("2d");
  ctx.clearRect(0, 0, effectCanvas.current.width, effectCanvas.current.height);
  // TODO: maybe make this configurable
  const lineWidth = 1;
  const halfLineWidth = Math.floor(lineWidth / 2);
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = getEffectColor(0.6);
  const matrixWidth = getMatrixWidth();
  // Draw crosshair
  ctx.beginPath();
  // Fix to get a crisp 1px wide line, see https://www.rgraph.net/canvas/docs/howto-get-crisp-lines-with-no-antialias.html
  ctx.translate(0.5, 0.5);
  ctx.moveTo(0, hoverPosInEffectCanvas.y);
  ctx.lineTo(hoverPosInEffectCanvas.x - crosshairGap, hoverPosInEffectCanvas.y);
  ctx.moveTo(hoverPosInEffectCanvas.x + crosshairGap, hoverPosInEffectCanvas.y);
  ctx.lineTo(matrixWidth, hoverPosInEffectCanvas.y);
  ctx.moveTo(hoverPosInEffectCanvas.x, 0);
  ctx.lineTo(hoverPosInEffectCanvas.x, hoverPosInEffectCanvas.y - crosshairGap);
  ctx.moveTo(hoverPosInEffectCanvas.x, hoverPosInEffectCanvas.y + crosshairGap);
  ctx.lineTo(hoverPosInEffectCanvas.x, matrixWidth);
  ctx.stroke();
  ctx.translate(-0.5, -0.5);

  // Draw transparent colored dot where the click position would be
  ctx.fillStyle = getEffectColor(0.6);
  ctx.fillRect(
    hoverPosInEffectCanvas.x - halfLineWidth,
    hoverPosInEffectCanvas.y - halfLineWidth,
    lineWidth,
    lineWidth,
  );
};

const onDragStart = (evt, dispatch, wkFrame) => {
  evt.dataTransfer.effectAllowed = "move";
  evt.dataTransfer.setData("text/html", "");
  evt.dataTransfer.setDragImage(document.getElementById("drag-preview"), 0, 0);
  dispatch({
    type: "SET_DRAG_START_POS",
    dragStartPos: {
      x: evt.clientX,
      y: evt.clientY,
    },
  });
  if (wkFrame.current != null) {
    // Workaround, otherwise the iframe will swallow the dragover events
    wkFrame.current.style.setProperty("pointer-events", "none");
  }
};

const onDrag = (evt, dispatch) => {
  const { clientX, clientY } = evt;
  dispatch({
    type: "SET_POSITION",
    positionUpdater: prevState => {
      const { position, dragStartPos } = prevState;
      const translateX = position.x - (clientX - dragStartPos.x);
      const translateY = position.y - (clientY - dragStartPos.y);
      return {
        x: translateX,
        y: translateY,
      };
    },
  });
  dispatch({
    type: "SET_DRAG_START_POS",
    dragStartPos: {
      x: clientX,
      y: clientY,
    },
  });
  evt.preventDefault();
};

const onDragEnd = wkFrame => {
  if (wkFrame.current != null) {
    wkFrame.current.style.setProperty("pointer-events", "auto");
  }
};

const createDeferredPromise = function() {
  let resolve = null;
  let reject = null;
  const p = new Promise((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });
  return Object.assign(p, { resolve, reject });
};

const generateMessageId = function() {
  return (
    Date.now().toString(36) +
    Math.random()
      .toString(36)
      .substr(2)
  );
};

type Props = {
  webKnossosUrl: string,
  initialConnectionIndex: number,
  initialSynapseIndex: number,
};

const promiseByMessageId = {};

const Connectome = ({ webKnossosUrl, initialConnectionIndex, initialSynapseIndex }: Props) => {
  const defaultData: ConnectomeData = {
    axonClasses: [],
    dendClasses: [],
    axons: [],
    dends: [],
    connections: [],
    synapseTypes: [],
  };
  const matrixWidth = getMatrixWidth();
  const matrixHeight = getMatrixHeight();
  const connectomeCanvas = useRef(null);
  const effectCanvas = useRef(null);
  const minimapCanvas = useRef(null);
  const wkFrame = useRef(null);
  const [isApiReady, setIsApiReady] = useState(false);
  const data = useFetch(fetchData, defaultData, []);
  const reducer = useCallback(
    (state, action) => {
      const { type, scale, position, dragStartPos, positionUpdater } = action;
      const getNewPosition = () => {
        const newPosition = positionUpdater ? positionUpdater(state) : position;
        const usedScale = scale || state.scale;
        return {
          x: clamp(newPosition.x, 0, data.dends.length * usedScale.x - matrixWidth),
          y: clamp(newPosition.y, 0, data.axons.length * usedScale.y - matrixHeight),
        };
      };
      switch (type) {
        case "SET_POSITION": {
          const newPosition = getNewPosition();
          return { ...state, position: newPosition };
        }
        case "SET_SCALE": {
          return { ...state, scale };
        }
        case "SET_DRAG_START_POS": {
          return { ...state, dragStartPos };
        }
        case "SET_POSITION_AND_SCALE": {
          const newPosition = getNewPosition();
          return { ...state, position: newPosition, scale };
        }
        default:
          throw new Error(
            "This type of update action for position and scale state is not supported.",
          );
      }
    },
    [data.axons.length, data.dends.length, matrixWidth, matrixHeight],
  );

  const [state, dispatch] = useReducer(reducer, {
    position: { x: 0, y: 0 },
    scale: { x: 1, y: 1 },
    dragStartPos: { x: 0, y: 0 },
  });
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [activeSynapse, setActiveSynapse] = useState(-1);
  const [currentSynapses, setCurrentSynapses] = useState([]);
  const [lastClickPos, setLastClickPos] = useState({ x: -1, y: -1 });
  const [lastHighlight, setLastHighlight] = useState({
    position: { x: -1, y: -1 },
    imageData: null,
  });
  const [positionToConnectionMap, setPositionToConnectionMap] = useState([]);
  const dendClassIntervals = useMemo(() => getClassIntervalsOf(data.dends, data.dendClasses), [
    data.dendClasses,
    data.dends,
  ]);

  const axonClassIntervals = useMemo(() => getClassIntervalsOf(data.axons, data.axonClasses), [
    data.axonClasses,
    data.axons,
  ]);
  const width = data.dends.length;
  const height = data.axons.length;
  const connectionRectangleSize = useMemo(() => calculateRectangleSizes(width, height, 10), [
    width,
    height,
  ]);
  const scaledWidth = width * state.scale.x;
  const scaledHeight = height * state.scale.y;

  // Make effectCanvas always the same size. Originally the idea was to scale it the same way as the
  // visible matrix and make the selection dot overlap the matrix entries
  // but as that doesn't work correctly, this way is more consistent for now.
  const effectCanvasWidth = 200;
  const effectCanvasHeight = 200;
  const effectCanvasScale = useMemo(
    () => ({
      x: matrixWidth / effectCanvasWidth,
      y: matrixHeight / effectCanvasHeight,
    }),
    [matrixWidth, matrixHeight],
  );
  useEffect(
    () =>
      drawConnectome(connectomeCanvas, data, setPositionToConnectionMap, connectionRectangleSize),
    [connectionRectangleSize, data],
  );
  useEffect(
    () =>
      drawHighlightedConnection(
        connectomeCanvas,
        connectionRectangleSize,
        lastClickPos,
        lastHighlight,
        setLastHighlight,
      ),
    [connectionRectangleSize, lastClickPos, lastHighlight],
  );
  const minimapWidth = getMinimapWidth();
  useEffect(() => drawMinimap(connectomeCanvas, minimapCanvas, minimapWidth), [data, minimapWidth]);

  useEffect(() => drawEffects(effectCanvas, hoverPos, effectCanvasScale), [
    effectCanvasScale,
    hoverPos,
  ]);

  const minScale = useMemo(
    () => ({ x: matrixWidth / data.dends.length, y: matrixWidth / data.axons.length }),
    [data.axons, data.dends, matrixWidth],
  );

  useEffect(() => {
    dispatch({ type: "SET_SCALE", scale: minScale });
  }, [minScale]);

  // We cannot use the onDrag event handler of the matrix element, because Firefox doesn't include
  // clientX/clientY values in the drag event. We have to use the document's dragover event handler instead.
  const onDocumentDragOverCallback = useCallback(evt => onDrag(evt, dispatch), []);
  useEffect(() => {
    document.addEventListener("dragover", onDocumentDragOverCallback, false);
    return () => document.removeEventListener("dragover", onDocumentDragOverCallback);
  }, [onDocumentDragOverCallback]);

  const [, updateState] = React.useState();
  const forceUpdate = useCallback(() => updateState({}), []);
  useEffect(() => {
    window.addEventListener("resize", forceUpdate);
    return () => window.removeEventListener("resize", forceUpdate);
  });

  const onHover = useCallback(
    (evt: SyntheticMouseEvent<HTMLCanvasElement> | SyntheticWheelEvent<HTMLCanvasElement>) => {
      if (effectCanvas.current == null) {
        return;
      }
      setHoverPos({ x: evt.clientX, y: evt.clientY });
    },
    [],
  );

  const onMessage = message => {
    const { message: messageString, type, messageId, returnValue } = message.data;
    if (type === "init") {
      setIsApiReady(true);
      return;
    }
    if (messageId == null || !promiseByMessageId[messageId]) return;

    if (type === "ack") {
      promiseByMessageId[messageId].resolve(returnValue);
    } else if (type === "err") {
      Toast.error(messageString, { position: "top-right" });
      promiseByMessageId[messageId].reject(messageString);
    }
    delete promiseByMessageId[messageId];
  };

  useEffect(() => {
    window.addEventListener("message", onMessage, false);
    return () => window.removeEventListener("message", onMessage);
  });

  const onScroll = useCallback(
    (evt: WheelEvent) => {
      const { scale } = state;
      const delta = evt.deltaY > 0 ? 0.95 : 1.05;
      const newScale = {
        x: Math.max(minScale.x, scale.x * delta),
        y: Math.max(minScale.y, scale.y * delta),
      };
      // TODO: Set the hover pos as well
      const positionUpdater = prevState => {
        // The event coordinates are relative to the effect canvas.
        // Thus we need to add the position of the connectome canvas.
        const { position, scale } = prevState;
        const scrollPosition = {
          x: evt.offsetX + position.x,
          y: evt.offsetY + position.y,
        };
        const newPosition = {
          x: scrollPosition.x * (newScale.x / scale.x),
          y: scrollPosition.y * (newScale.y / scale.y),
        };
        return {
          x: position.x + newPosition.x - scrollPosition.x,
          y: position.y + newPosition.y - scrollPosition.y,
        };
      };
      dispatch({
        type: "SET_POSITION_AND_SCALE",
        scale: newScale,
        positionUpdater,
      });
      // We also call the hover function to update the hover position.
      onHover(evt);
      evt.preventDefault();
    },
    [state, minScale.x, minScale.y, onHover],
  );

  useEffect(() => {
    const currentCanvas = effectCanvas.current;
    if (currentCanvas != null) {
      currentCanvas.addEventListener("wheel", onScroll, { passive: false });
      return () => currentCanvas.removeEventListener("wheel", onScroll);
    }
  }, [data, onScroll]);

  const sendMessageToWkIFrame = useCallback(
    (type: string, args: Array<any>) => {
      const messageId = generateMessageId();
      const promise = createDeferredPromise();
      promiseByMessageId[messageId] = promise;
      if (wkFrame.current) {
        wkFrame.current.contentWindow.postMessage(
          {
            type,
            args,
            messageId,
          },
          "*",
        );
      }
      return promise;
    },
    [wkFrame],
  );

  const setMapping = useCallback(
    (mapping: Object, config: Object) => sendMessageToWkIFrame("setMapping", [mapping, config]),
    [sendMessageToWkIFrame],
  );
  const setActiveTreeByName = useCallback(
    (treeName: string) => sendMessageToWkIFrame("setActiveTreeByName", [treeName]),
    [sendMessageToWkIFrame],
  );
  const importNml = useCallback((nml: string) => sendMessageToWkIFrame("importNml", [nml]), [
    sendMessageToWkIFrame,
  ]);

  const resetSkeleton = useCallback(() => sendMessageToWkIFrame("resetSkeleton", []), [
    sendMessageToWkIFrame,
  ]);
  const resetMeshes = useCallback(() => sendMessageToWkIFrame("resetMeshes", []), [
    sendMessageToWkIFrame,
  ]);

  const loadPrecomputedMesh = useCallback(
    (segmentId, seedPosition) =>
      sendMessageToWkIFrame("loadPrecomputedMesh", [segmentId, seedPosition]),
    [sendMessageToWkIFrame],
  );

  const getAvailableMeshFiles = useCallback(
    () => sendMessageToWkIFrame("getAvailableMeshFiles", []),
    [sendMessageToWkIFrame],
  );
  const getActiveMeshFile = useCallback(() => sendMessageToWkIFrame("getActiveMeshFile", []), [
    sendMessageToWkIFrame,
  ]);
  const setActiveMeshFile = useCallback(
    meshFileName => sendMessageToWkIFrame("setActiveMeshFile", [meshFileName]),
    [sendMessageToWkIFrame],
  );

  const ensureActiveMeshFile = useCallback(async () => {
    const activeMeshFile = await getActiveMeshFile();
    if (activeMeshFile != null) {
      return true;
    }

    const availableMeshFiles = await getAvailableMeshFiles();
    if (availableMeshFiles.length > 0) {
      await setActiveMeshFile(availableMeshFiles[0]);
      return true;
    } else {
      return false;
    }
  }, [getActiveMeshFile, getAvailableMeshFiles, setActiveMeshFile]);

  const setSynapticConnectionActive = useCallback(
    async (index: number, synapseIndex: number = 0) => {
      const connection = data.connections[index];
      if (!connection) {
        return;
      }
      const axonId = connection.axon;
      const dendId = connection.dend;

      const axonAgglomerateId = data.axons[axonId][1];
      const dendAgglomerateId = data.dends[dendId][1];

      const synapses = connection.synapses;
      setLastClickPos({ x: dendId, y: axonId });
      const [axonNml, dendNml, synapseNml] = await Promise.all([
        (await fetchAxonNml(axonAgglomerateId)).text(),
        (await fetchDendNml(dendAgglomerateId)).text(),
        (await fetchSynapsesNml(axonAgglomerateId, dendAgglomerateId)).text(),
      ]);
      const isAxonNmlValid = checkForNml(axonNml);
      const isDendNmlValid = checkForNml(dendNml);
      const isSynapseNmlValid = checkForNml(synapseNml);
      if (isAxonNmlValid && isDendNmlValid && isSynapseNmlValid) {
        resetSkeleton();
        resetMeshes();
        // Await the nml imports to make sure that the tree exists once setActiveTreeByName is called
        await importNml(synapseNml);

        setMapping(
          {
            [dendAgglomerateId]: dendAgglomerateId,
            [axonAgglomerateId]: axonAgglomerateId,
          },
          { hideUnmappedIds: true },
        );

        const synapseTypeId = synapses[synapseIndex];
        const synapseType = data.synapseTypes[synapseTypeId];
        setActiveTreeByName(`${synapseType}_synapse_${synapseIndex + 1}`);

        const hasActiveMeshFile = await ensureActiveMeshFile();
        if (hasActiveMeshFile) {
          // The seed position is simply set to [0, 0, 0] since it is only used when the user expands the
          // sidebar, clicks on the meshes tab and clicks a segment. In that case, the position jumps to the provided seed.
          // Additionally, since the dendrite/axon pair can have multiple synapses, it's not clear seed position should be used.
          // webKnossos might infer the seed position automatically in the future, anyhow.
          const seedPosition = [0, 0, 0];

          await loadPrecomputedMesh(dendAgglomerateId, seedPosition);
          await loadPrecomputedMesh(axonAgglomerateId, seedPosition);
        } else {
          await importNml(axonNml);
          await importNml(dendNml);
        }

        setCurrentSynapses(synapses);
        setActiveSynapse(synapseIndex);
        return;
      }
      const errorMessage = kind => `No valid nml file containing the skeleton of the ${kind} found on the server!
        Please use the Voxelytics VisualizeConnectomeTask to generate the correct nml files and copy them to the public folder.`;
      if (!isAxonNmlValid) {
        Toast.error(errorMessage("axon"), { position: "top-right" });
        console.warn("Received axon data:", axonNml);
      }
      if (!isDendNmlValid) {
        Toast.error(errorMessage("dendrite"), { position: "top-right" });
        console.warn("Received dendrite data:", dendNml);
      }
      if (!isSynapseNmlValid) {
        Toast.error(errorMessage("synapses"), { position: "top-right" });
        console.warn("Received synapse data:", synapseNml);
      }
      console.warn(
        "Please use the Voxelytics VisualizeConnectomeTask to generate the correct nml files and copy them to the public folder.",
      );
    },
    [
      data.connections,
      data.synapseTypes,
      data.axons,
      data.dends,
      importNml,
      resetSkeleton,
      setActiveTreeByName,
      setMapping,
    ],
  );

  useEffect(() => {
    if (isApiReady) {
      setSynapticConnectionActive(initialConnectionIndex, initialSynapseIndex);
    }
  }, [data, initialConnectionIndex, initialSynapseIndex, isApiReady, setSynapticConnectionActive]);

  const findEntryAroundPosition = (xPos, yPos) => {
    let minX = xPos,
      maxX = xPos;
    let minY = yPos,
      maxY = yPos;
    let entry = INVALID_ENTRY;
    const setEntryToConnectionAt = (x, y) => {
      entry = positionToConnectionMap[y * data.dends.length + x];
    };
    // Searching for a connection in form of a clockwise spiral around the given position.
    for (let i = 0; i < 200 && entry === INVALID_ENTRY; ++i) {
      for (let x = minX; x <= maxX && entry === INVALID_ENTRY; ++x) {
        setEntryToConnectionAt(x, minY);
      }
      for (let y = minY; y <= maxY && entry === INVALID_ENTRY; ++y) {
        setEntryToConnectionAt(maxX, y);
      }
      for (let x = maxX; x >= minX && entry === INVALID_ENTRY; --x) {
        setEntryToConnectionAt(x, maxY);
      }
      for (let y = maxY; y >= minY && entry === INVALID_ENTRY; --y) {
        setEntryToConnectionAt(minX, y);
      }
      --minX;
      --minY;
      ++maxX;
      ++maxY;
    }
    return entry;
  };

  const onClick = async (evt: SyntheticMouseEvent<HTMLCanvasElement>) => {
    if (effectCanvas.current == null || connectomeCanvas.current == null) {
      return;
    }
    const { connections } = data;
    const evtPosition = { x: evt.clientX, y: evt.clientY };
    const positionInConnectomeCanvas = evtPositionToCanvasPosition(
      evtPosition,
      connectomeCanvas.current,
      scale,
    );
    const connectionIndex = findEntryAroundPosition(
      positionInConnectomeCanvas.x,
      positionInConnectomeCanvas.y,
    );
    if (connectionIndex < 0 || connectionIndex > connections.length - 1) {
      Toast.warn("No contact area between the selected cells.", { position: "top-right" });
      return;
    }
    setSynapticConnectionActive(connectionIndex);
  };

  /* We need this reference to the current synapses because otherwise updateActiveSynapseBy would
   * always use the initial value of currentSynapses. See https://github.com/facebook/react/issues/14031*/

  const currentSynapsesRef = useRef();
  useEffect(() => {
    currentSynapsesRef.current = currentSynapses;
  }, [currentSynapses]);

  if (width === 0) return null;

  const zoomFactor = scaledWidth / matrixWidth;
  const { position, scale } = state;
  const positionInConnectomeCanvas = connectomeCanvas.current
    ? evtPositionToCanvasPosition(hoverPos, connectomeCanvas.current, scale)
    : { x: 0, y: 0 };

  const iFrameWidth = getIFrameWidth();
  const usageInstructionFontSize = getUsageInstructionFontSize();
  const legendFontSize = Math.min(usageInstructionFontSize, 12);

  const updateActiveSynapseBy = step =>
    setActiveSynapse(previousActiveSynapseIndex => {
      const synapses = currentSynapsesRef.current;
      if (!synapses || synapses.length === 0) {
        return previousActiveSynapseIndex;
      }
      const synapseCount = synapses.length;
      const nextIndex = mod(previousActiveSynapseIndex + step, synapseCount);
      const nextSynapseTypeId = synapses[nextIndex];
      const nextSynapseType = data.synapseTypes[nextSynapseTypeId];
      setActiveTreeByName(`${nextSynapseType}_synapse_${nextIndex + 1}`);
      return nextIndex;
    });

  const keyHandlers = {
    jumpToNextSynapse: () => updateActiveSynapseBy(1),
    jumpToPreviousSynapse: () => updateActiveSynapseBy(-1),
  };

  const isLoading = positionToConnectionMap.length === 0;

  /* Note: The y-axis is rendered exactly like the x-axis, except that it has an additional div-container,
   * that transforms the axis into the correct position. This causes the caption text to be misoriented.
   * This is solved by mirroring the letters of the y axis.
   */
  return (
    <React.Fragment>
      <GlobalHotKeys handlers={keyHandlers} keyMap={keyMap} />
      {isLoading ? (
        <div className="loading-container">
          <div className="loading" />
        </div>
      ) : null}
      <div className="card">
        <div style={{ display: "flex" }}>
          <div
            style={{
              marginTop: -AXIS_DESCRIPTION_HEIGHT /* needed because the dendrite axis description leaves a whitespace of 24*/,
            }}
          >
            <div style={{ top: AXIS_DESCRIPTION_HEIGHT, position: "relative" }}>
              Postsynaptic targets
            </div>
            <div className="y-axis-transformation-container" style={{ zIndex: 5 }}>
              <div style={{ transform: "scale(-1, 1)" }}>Presynaptic axons</div>
            </div>
            <div
              style={{
                position: "relative",
                width: Y_AXIS_WIDTH + matrixWidth,
                height: X_AXIS_HEIGHT + matrixHeight,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  width: matrixWidth,
                  height: X_AXIS_HEIGHT,
                  overflow: "hidden",
                  left: Y_AXIS_WIDTH,
                  top: 0,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: -position.x,
                    display: "flex",
                    flexDirection: "column",
                    height: X_AXIS_HEIGHT,
                  }}
                >
                  <div
                    style={{ display: "flex", height: X_AXIS_HEIGHT }}
                    className="axis-caption-container"
                  >
                    {renderCaptionOfAxis(
                      dendClassIntervals,
                      scale.x,
                      positionInConnectomeCanvas.x,
                      false,
                    )}
                  </div>
                </div>
              </div>

              <div className="y-axis-transformation-container">
                <div
                  style={{
                    position: "absolute",
                    width: matrixWidth,
                    height: X_AXIS_HEIGHT,
                    overflow: "hidden",
                    left: Y_AXIS_WIDTH,
                    top: 0,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: -position.y,
                      display: "flex",
                      flexDirection: "column",
                      height: X_AXIS_HEIGHT,
                    }}
                  >
                    <div
                      style={{ display: "flex", height: X_AXIS_HEIGHT }}
                      className="axis-caption-container"
                    >
                      {renderCaptionOfAxis(
                        axonClassIntervals,
                        scale.y,
                        positionInConnectomeCanvas.y,
                        true,
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  overflow: "hidden",
                  position: "absolute",
                  top: X_AXIS_HEIGHT,
                  left: Y_AXIS_WIDTH,
                }}
              >
                <div
                  style={{
                    position: "relative",
                    left: -position.x,
                    top: -position.y,
                  }}
                >
                  <canvas
                    id="connectome-canvas"
                    style={{
                      width: scaledWidth,
                      height: scaledHeight,
                    }}
                    className={scale.x > 1 || scale.y > 1 ? "pixelated" : ""}
                    ref={connectomeCanvas}
                    width={width}
                    height={height}
                  />
                </div>
                <canvas
                  id="effect-canvas"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: matrixWidth,
                    height: matrixHeight,
                    cursor: "none",
                    imageRendering: "pixelated",
                  }}
                  className={scale.x > 1 || scale.y > 1 ? "pixelated" : ""}
                  ref={effectCanvas}
                  width={effectCanvasWidth}
                  height={effectCanvasHeight}
                  onMouseMove={onHover}
                  onClick={onClick}
                  draggable={true}
                  onDragStart={evt => onDragStart(evt, dispatch, wkFrame)}
                  onDragEnd={() => onDragEnd(wkFrame)}
                />
              </div>
            </div>
            <div
              style={{
                marginTop: 4,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-evenly",
              }}
            >
              <table className="color-schema-table" style={{ fontSize: legendFontSize }}>
                <tbody>
                  <tr>
                    <td>Number of synapses</td>
                    <td>1</td>
                    <td>2</td>
                    <td>3</td>
                    <td>4</td>
                    <td>≥5</td>
                  </tr>
                  <tr>
                    <td>Color</td>
                    <td>
                      <div
                        className="color-schema-example-div"
                        style={{ backgroundColor: "rgb(69, 58, 150)" }}
                      />
                    </td>
                    <td>
                      <div
                        className="color-schema-example-div"
                        style={{ backgroundColor: "rgb(76, 115, 183)" }}
                      />
                    </td>
                    <td>
                      <div
                        className="color-schema-example-div"
                        style={{ backgroundColor: "rgb(32, 187, 183)" }}
                      />
                    </td>
                    <td>
                      <div
                        className="color-schema-example-div"
                        style={{ backgroundColor: "rgb(199, 193, 46)" }}
                      />
                    </td>
                    <td>
                      <div
                        className="color-schema-example-div"
                        style={{ backgroundColor: "rgb(242, 233, 38)" }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ position: "relative", marginLeft: 20 }}>
            <canvas
              id="minimap"
              width={minimapWidth}
              height={minimapWidth}
              ref={minimapCanvas}
              style={{ border: "1px solid" }}
            />
            <div
              style={{
                position: "absolute",
                border: "solid 1px gray",
                background: "rgba(100, 100, 100, 0.1)",
                width: minimapWidth / zoomFactor,
                height: minimapWidth / zoomFactor,
                left: (position.x / scaledWidth) * minimapWidth,
                top: (position.y / scaledWidth) * minimapWidth,
              }}
            />
            <div
              style={{
                fontSize: usageInstructionFontSize,
                width: minimapWidth,
                marginTop: usageInstructionFontSize,
              }}
            >
              <div style={{ fontSize: usageInstructionFontSize }}>
                Synapse {activeSynapse + 1} out of {currentSynapses.length} <br />
                <button
                  onClick={() => updateActiveSynapseBy(-1)}
                  className="synapse-navigation-buttons"
                  style={{ marginRight: usageInstructionFontSize }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  onClick={() => updateActiveSynapseBy(1)}
                  className="synapse-navigation-buttons"
                  style={{ marginLeft: usageInstructionFontSize }}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
              <h4 style={{ fontWeight: "bold", marginTop: usageInstructionFontSize * 2 }}>
                Getting started
              </h4>
              <ul className="usage-instructions-list">
                <li>
                  Click on a connectome entry on the left to show the corresponding axon, dendrite
                  and synapses in the electron microscopy (EM) volume on the right.
                </li>
                <li>
                  Use the mouse wheel and left-click-drag to navigate the connectome and EM volume.
                </li>
                <li>
                  Use the left and right arrow keys or the buttons above to jump between synapses.
                </li>
              </ul>
            </div>
          </div>
          <div style={{ width: iFrameWidth }}>
            <iframe
              src={webKnossosUrl}
              ref={wkFrame}
              style={{
                marginLeft: 20,
                width: iFrameWidth,
                height: iFrameWidth,
              }}
              title="webKnossos"
              id="wk-frame"
              name="wk-frame"
              allow="clipboard-write"
            />
            <div className="synapse-color-legend" style={{ fontSize: legendFontSize }}>
              <div className="synapse-color-entry">
                <div
                  className="synapse-color-schema-example-div"
                  style={{
                    backgroundColor: "rgb(255, 65, 54)",
                    width: usageInstructionFontSize,
                    height: usageInstructionFontSize,
                  }}
                />
                <span>Axon</span>
              </div>
              <div className="synapse-color-entry">
                <div
                  className="synapse-color-schema-example-div"
                  style={{
                    backgroundColor: "rgb(46,204,64)",
                    width: usageInstructionFontSize,
                    height: usageInstructionFontSize,
                  }}
                />
                <span>Dendrite / Postsynaptic target</span>
              </div>
              <div className="synapse-color-entry">
                <div
                  className="synapse-color-schema-example-div"
                  style={{
                    backgroundColor: "rgb(4, 53, 47)",
                    width: usageInstructionFontSize,
                    height: usageInstructionFontSize,
                  }}
                />
                <span>Primary Spine</span>
              </div>
              <div className="synapse-color-entry">
                <div
                  className="synapse-color-schema-example-div"
                  style={{
                    backgroundColor: "rgb(136, 15, 96)",
                    width: usageInstructionFontSize,
                    height: usageInstructionFontSize,
                  }}
                />
                <span>Secondary Spine</span>
              </div>
              <div className="synapse-color-entry">
                <div
                  className="synapse-color-schema-example-div"
                  style={{
                    backgroundColor: "rgb(240, 103, 11)",
                    width: usageInstructionFontSize,
                    height: usageInstructionFontSize,
                  }}
                />
                <span>Shaft</span>
              </div>
              <div className="synapse-color-entry">
                <div
                  className="synapse-color-schema-example-div"
                  style={{
                    backgroundColor: "rgb(240, 53, 131)",
                    width: usageInstructionFontSize,
                    height: usageInstructionFontSize,
                  }}
                />
                <span>Soma</span>
              </div>
              <Tooltip
                content="Synapse Types by Color"
                placement="top"
                background="rgba(1.0, 1.0, 1.0, 0.9)"
                className="description-tooltip"
                style={{ marginLeft: 5 }}
              >
                <FontAwesomeIcon icon={faInfoCircle} color="#aaa" />
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Connectome;
